// Design UI state: the active tool and dock tab, plus the tool and dock catalogues (DRAW_PLAN §1, §10).
// A tool or dock is "ready" once its milestone lands; until then its button is shown disabled.
import { create } from 'zustand';
import type { ComponentType } from 'react';
import type { MessageKey } from '../../i18n';
import type { Appearance } from '../model/types';
import { defaultAppearance } from '../model/defaults';
import { DEFAULT_SNAP, type SnapOptions } from '../geometry/snapping';
import { HistoryDock } from './docks/HistoryDock';
import { LayersDock } from './docks/LayersDock';
import { PropertiesDock } from './docks/PropertiesDock';
import { ArrangeDock } from './docks/AlignPanel';
import { AppearanceDock } from './docks/AppearanceDock';
import { SwatchesDock } from './docks/SwatchesDock';
import { AdDock } from './ad/AdDock';

export type ToolId =
  | 'select'
  | 'direct'
  | 'lasso'
  | 'pen'
  | 'addAnchor'
  | 'deleteAnchor'
  | 'convertAnchor'
  | 'curvature'
  | 'pencil'
  | 'scissors'
  | 'knife'
  | 'rect'
  | 'ellipse'
  | 'polygon'
  | 'star'
  | 'line'
  | 'type'
  | 'areaType'
  | 'pathType'
  | 'rotate'
  | 'reflect'
  | 'scale'
  | 'shear'
  | 'eyedropper'
  | 'gradient'
  | 'artboard'
  | 'hand'
  | 'zoom';

export type ToolDef = {
  id: ToolId;
  label: MessageKey;
  key: string;
  icon: string;
  hint: MessageKey;
  ready: boolean;
};

const T = (
  id: ToolId,
  label: MessageKey,
  key: string,
  icon: string,
  hint: MessageKey,
  ready = false,
): ToolDef => ({
  id,
  label,
  key,
  icon,
  hint,
  ready,
});

/** Tool bar order (DRAW_PLAN §1). `ready` flips to true in the milestone that builds the tool. */
export const TOOLS: ToolDef[] = [
  T('select', 'toolSelect', 'V', '➤', 'hintSelect', true),
  T('direct', 'toolDirect', 'A', '⌖', 'hintDirect', true),
  T('lasso', 'toolLasso', 'Q', '◌', 'hintLasso', true),
  T('pen', 'toolPen', 'P', '✒', 'hintPen', true),
  T('curvature', 'toolCurvature', 'Shift+~', '∿', 'hintCurvature', true),
  T('pencil', 'toolPencil', 'N', '✎', 'hintPencil', true),
  T('addAnchor', 'toolAddAnchor', '+', '+', 'hintAddAnchor', true),
  T('deleteAnchor', 'toolDeleteAnchor', '-', '−', 'hintDeleteAnchor', true),
  T('convertAnchor', 'toolConvertAnchor', 'Shift+C', '⌃', 'hintConvertAnchor', true),
  T('scissors', 'toolScissors', 'C', '✂', 'hintScissors', true),
  T('knife', 'toolKnife', 'K', '⟋', 'hintKnife', true),
  T('rect', 'toolRect', 'M', '▭', 'hintRect', true),
  T('ellipse', 'toolEllipse', 'L', '◯', 'hintEllipse', true),
  T('polygon', 'toolPolygon', '', '⬡', 'hintPolygon', true),
  T('star', 'toolStar', '', '☆', 'hintStar', true),
  T('line', 'toolLine', '\\', '╱', 'hintLine', true),
  T('type', 'toolType', 'T', 'T', 'hintType', true),
  T('areaType', 'toolAreaType', '', '⊤', 'hintAreaType'),
  T('pathType', 'toolPathType', '', '〰', 'hintPathType'),
  T('rotate', 'toolRotate', 'R', '⟳', 'hintRotate'),
  T('reflect', 'toolReflect', 'O', '⇋', 'hintReflect'),
  T('scale', 'toolScale', 'S', '⤢', 'hintScale'),
  T('shear', 'toolShear', '', '▱', 'hintShear'),
  T('eyedropper', 'toolEyedropper', 'I', '⌀', 'hintEyedropper', true),
  T('gradient', 'toolGradient', 'G', '◐', 'hintGradient'),
  T('artboard', 'toolArtboard', 'Shift+O', '▣', 'hintArtboard'),
  T('hand', 'toolHand', 'H', '✋', 'hintHand', true),
  T('zoom', 'toolZoom', 'Z', '🔍', 'hintZoom', true),
];

export type DockId =
  | 'ad'
  | 'properties'
  | 'layers'
  | 'appearance'
  | 'swatches'
  | 'type'
  | 'arrange'
  | 'artboards'
  | 'symbols'
  | 'menu'
  | 'history';
export type DockDef = { id: DockId; label: MessageKey; body: ComponentType | null };

export const DOCKS: DockDef[] = [
  { id: 'ad', label: 'dockAd', body: AdDock },
  { id: 'properties', label: 'dockProperties', body: PropertiesDock },
  { id: 'layers', label: 'dockLayers', body: LayersDock },
  { id: 'appearance', label: 'dockAppearance', body: AppearanceDock },
  { id: 'swatches', label: 'dockSwatches', body: SwatchesDock },
  { id: 'type', label: 'dockType', body: null },
  { id: 'arrange', label: 'dockArrange', body: ArrangeDock },
  { id: 'artboards', label: 'dockArtboards', body: null },
  { id: 'symbols', label: 'dockSymbols', body: null },
  { id: 'menu', label: 'dockMenu', body: null },
  { id: 'history', label: 'dockHistory', body: HistoryDock },
];

/** Later milestones register their dock bodies and mark their tools ready here. */
export function registerDock(id: DockId, body: ComponentType) {
  DOCKS.find((d) => d.id === id)!.body = body;
}
export function markReady(...ids: ToolId[]) {
  for (const t of TOOLS) if (ids.includes(t.id)) t.ready = true;
}

type UiState = {
  tool: ToolId;
  dock: DockId;
  selection: string[];
  /** Which colour box is active (Illustrator's fill / stroke boxes): swatches and the picker edit it. */
  target: 'fill' | 'stroke';
  /** The appearance new objects get; set by editing with nothing selected or by the eyedropper. */
  current: Appearance;
  /** Anchors selected with Direct selection / Lasso (node id, subpath, index). */
  anchors: { id: string; sp: number; i: number }[];
  /** Snapping (View): master switch + each kind; grid / guides shown on the canvas. */
  snap: SnapOptions & { enabled: boolean };
  showGrid: boolean;
  showGuides: boolean;
  /** The key object for Align (clicked again while selected). */
  keyObject: string | null;
};
export const useDesignUi = create<UiState & { set: (p: Partial<UiState>) => void }>((set) => ({
  tool: 'select',
  dock: 'properties',
  selection: [],
  target: 'fill',
  current: defaultAppearance(),
  anchors: [],
  snap: { ...DEFAULT_SNAP, enabled: true },
  showGrid: false,
  showGuides: true,
  keyObject: null,
  set: (p) => set(p),
}));

/** Tool changes end whatever the previous tool was in the middle of (an unfinished pen path …). */
useDesignUi.subscribe((st, prev) => {
  if (st.tool !== prev.tool) void import('../tools/registry').then((r) => r.toolFor(prev.tool)?.cancel?.());
});
